import { CloudinaryUploadResponse, UploadFile } from '../types';

import { FUNCTIONS_BASE_URL } from '../config/apiConfig';

const API_BASE = FUNCTIONS_BASE_URL;

type AwsUploadTargetResponse = {
  uploadUrl: string;
  cdnUrl: string;
  s3Key: string;
  headers?: Record<string, string>;
  uploadSource?: string;
  serverTime?: string;
};

// Details captured from a failed browser -> S3 PUT. These responses never reach
// Cloud Functions, so we attach them to the rejection and beacon them to
// logClientUploadError for server-side visibility.
type S3PutFailure = {
  httpStatus: number;
  responseBody?: string;
  awsRequestId?: string;
  awsId2?: string;
  reason: string;
};

export type UploadProgressDetails = {
  loadedBytes: number;
  totalBytes: number;
  speedBytesPerSecond: number;
};

class S3PutError extends Error {
  failure: S3PutFailure;
  constructor(failure: S3PutFailure) {
    super(`S3 PUT failed (status ${failure.httpStatus}): ${failure.reason}`);
    this.name = 'S3PutError';
    this.failure = failure;
  }
}

// S3 error responses are XML: <Error><Code>RequestTimeTooSkewed</Code>...</Error>
function parseS3ErrorCode(responseBody?: string): string | undefined {
  if (!responseBody) return undefined;
  const match = responseBody.match(/<Code>([^<]+)<\/Code>/);
  return match ? match[1] : undefined;
}

export interface StudioS3UploadOptions {
  onProgress?: (progress: number, details?: UploadProgressDetails) => void;
  onFileStart?: (uploadId: string) => void;
  onFileProgress?: (
    uploadId: string,
    progress: number,
    details?: UploadProgressDetails
  ) => void;
  onFileComplete?: (uploadId: string, response: CloudinaryUploadResponse, uploadData: any) => Promise<void>;
  onFileError?: (uploadId: string, error: string) => void;
  maxRetries?: number;
  retryDelay?: number;
  useChunkedUpload?: boolean;
  chunkSize?: number;
}

export class StudioS3Uploader {
  private static readonly DEFAULT_OPTIONS: Required<StudioS3UploadOptions> = {
    onProgress: () => {},
    onFileStart: () => {},
    onFileProgress: () => {},
    onFileComplete: async () => {},
    onFileError: () => {},
    maxRetries: 3,
    retryDelay: 1000,
    useChunkedUpload: false,
    chunkSize: 1024 * 1024, // 1MB chunks
  };

  static async uploadFile(
    file: File,
    uploadId: string,
    sessionId: string,
    sessionKey: string,
    options: StudioS3UploadOptions = {}
  ): Promise<CloudinaryUploadResponse> {
    const opts = { ...this.DEFAULT_OPTIONS, ...options };

    console.log(`☁️ Starting S3 upload for ${file.name} (${Math.round(file.size / 1024)}KB)`);
    
    return await this.uploadStandard(file, uploadId, sessionId, sessionKey, opts);
  }

  private static async uploadStandard(
    file: File,
    uploadId: string,
    sessionId: string,
    sessionKey: string,
    options: Required<StudioS3UploadOptions>
  ): Promise<CloudinaryUploadResponse> {
    let lastAttempt = 0;
    let lastFailure: S3PutFailure | undefined;
    let lastS3Key: string | undefined;
    let lastContentType: string | undefined;
    let clockSkewMs: number | undefined;

    for (let attempt = 1; attempt <= options.maxRetries; attempt++) {
      lastAttempt = attempt;
      
      try {
        console.log(`☁️ AWS upload attempt ${attempt}/${options.maxRetries} for ${file.name}`);

        const uploadTarget = await this.getAwsUploadTarget(
          sessionId,
          sessionKey,
          file.name,
          uploadId
        );

        lastS3Key = uploadTarget.s3Key;
        lastContentType = uploadTarget.headers?.['Content-Type'];

        // Compare the server's signing clock to ours. A large delta is the
        // classic cause of presign-OK-but-PUT-403 (RequestTimeTooSkewed).
        if (uploadTarget.serverTime) {
          const serverMs = Date.parse(uploadTarget.serverTime);
          if (!Number.isNaN(serverMs)) {
            clockSkewMs = Date.now() - serverMs;
          }
        }

        await this.makeXHRPutRequest(
          uploadTarget.uploadUrl,
          file,
          uploadTarget.headers || {},
          options.onProgress
        );

        console.log(`✅ AWS upload successful for ${file.name} on attempt ${attempt}`);
        return {
          secure_url: uploadTarget.cdnUrl,
          public_id: uploadTarget.s3Key,
          context: undefined,
          bytes: file.size,
          format: file.type,
        };
        
      } catch (error) {
        console.warn(`❌ AWS upload attempt ${attempt} failed for ${file.name}:`, error);

        if (error instanceof S3PutError) {
          lastFailure = error.failure;
        } else {
          lastFailure = {
            httpStatus: 0,
            reason: error instanceof Error ? error.message : String(error),
          };
        }

        if (attempt < options.maxRetries) {
          const jitter = Math.random() * options.retryDelay;
          const delay = options.retryDelay * Math.pow(2, attempt - 1) + jitter;
          console.log(`⏳ Retrying upload for ${file.name} in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    // All retries exhausted: beacon the failure detail so it is queryable
    // server-side (browser -> S3 PUT failures never hit Cloud Functions).
    this.reportFailure({
      sessionId,
      key: sessionKey,
      uploadId,
      s3Key: lastS3Key,
      filename: file.name,
      fileSize: file.size,
      contentType: lastContentType,
      uploadSource: 'AWS',
      httpStatus: lastFailure?.httpStatus,
      s3ErrorCode: parseS3ErrorCode(lastFailure?.responseBody),
      responseBody: lastFailure?.responseBody,
      awsRequestId: lastFailure?.awsRequestId,
      awsId2: lastFailure?.awsId2,
      clockSkewMs,
      attempts: lastAttempt,
      reason: lastFailure?.reason,
    });

    throw new Error(`Upload failed after ${lastAttempt} attempts for ${file.name}`);
  }

  // Fire-and-forget failure telemetry. Uses sendBeacon so reports survive the
  // user closing the tab; never throws (telemetry must not break uploads).
  private static reportFailure(payload: Record<string, unknown>): void {
    try {
      const url = `${API_BASE}/logClientUploadError`;
      const body = JSON.stringify(payload);

      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        const blob = new Blob([body], { type: 'application/json' });
        if (navigator.sendBeacon(url, blob)) return;
      }

      // Fallback for environments without sendBeacon.
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
        credentials: 'omit',
      }).catch(() => {});
    } catch (error) {
      console.warn('⚠️ Failed to report upload failure telemetry:', error);
    }
  }

  private static async getAwsUploadTarget(
    sessionId: string,
    sessionKey: string,
    filename: string,
    uploadId: string
  ): Promise<AwsUploadTargetResponse> {
    const response = await fetch(`${API_BASE}/generateDirectS3UploadUrl`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId,
        key: sessionKey,
        filename,
        uploadId,
      }),
    });

    if (!response.ok) {
      const body = await response.text();
      throw new Error(`Failed to generate AWS upload URL (${response.status}): ${body}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to generate AWS upload URL');
    }

    return {
      uploadUrl: data.uploadUrl,
      cdnUrl: data.cdnUrl,
      s3Key: data.s3Key,
      headers: data.headers,
      uploadSource: data.uploadSource,
      serverTime: data.serverTime,
    };
  }

  private static makeXHRPutRequest(
    url: string,
    file: File,
    headers: Record<string, string>,
    onProgress: (progress: number, details?: UploadProgressDetails) => void
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let lastLoaded = 0;
      let lastProgressAt = Date.now();
      let smoothedSpeed = 0;

      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const now = Date.now();
          const elapsedSeconds = Math.max((now - lastProgressAt) / 1000, 0.001);
          const instantSpeed = (e.loaded - lastLoaded) / elapsedSeconds;
          smoothedSpeed = smoothedSpeed === 0
            ? instantSpeed
            : smoothedSpeed * 0.7 + instantSpeed * 0.3;
          lastLoaded = e.loaded;
          lastProgressAt = now;

          const progress = e.loaded / e.total;
          onProgress(progress, {
            loadedBytes: e.loaded,
            totalBytes: e.total,
            speedBytesPerSecond: smoothedSpeed,
          });
        }
      });

      // x-amz-request-id / x-amz-id-2 let AWS support trace the exact request.
      // They are only readable if the bucket CORS exposes them via
      // Access-Control-Expose-Headers (getResponseHeader returns null otherwise).
      const awsHeaders = () => ({
        awsRequestId: xhr.getResponseHeader('x-amz-request-id') || undefined,
        awsId2: xhr.getResponseHeader('x-amz-id-2') || undefined,
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(true);
        } else {
          reject(new S3PutError({
            httpStatus: xhr.status,
            responseBody: xhr.responseText || undefined,
            reason: `http_${xhr.status}`,
            ...awsHeaders(),
          }));
        }
      });

      xhr.addEventListener('error', () => {
        // status is typically 0 here: network failure, CORS block, or the PUT
        // was dropped before a response (proxy/VPN/ad-blocker).
        reject(new S3PutError({
          httpStatus: xhr.status,
          reason: 'network_error',
          ...awsHeaders(),
        }));
      });

      xhr.addEventListener('timeout', () => {
        reject(new S3PutError({
          httpStatus: xhr.status,
          reason: 'timeout',
          ...awsHeaders(),
        }));
      });

      // Set timeout (longer for iOS Safari)
      xhr.timeout = 5 * 60 * 1000; // 5 minutes

      xhr.open('PUT', url);
      Object.entries(headers).forEach(([key, value]) => {
        // S3 presigned URLs already encode ACL in query params; sending
        // x-amz-acl header causes SignatureDoesNotMatch / AccessDenied.
        if (key.toLowerCase() === 'x-amz-acl') return;
        xhr.setRequestHeader(key, value);
      });
      xhr.send(file);
    });
  }

  static async uploadBatch(
    uploads: UploadFile[],
    sessionId: string,
    sessionKey: string,
    maxConcurrent: number = 3,
    options: StudioS3UploadOptions = {}
  ): Promise<void> {
    console.log(`☁️ Starting batch upload of ${uploads.length} files (max concurrent: ${maxConcurrent})`);
    
    const opts = { ...this.DEFAULT_OPTIONS, ...options };
    const pendingUploads = uploads.filter(u => !u.downloadUrl);
    
    // Process uploads in batches to respect concurrency limits while preserving order
    for (let i = 0; i < pendingUploads.length; i += maxConcurrent) {
      const batch = pendingUploads.slice(i, i + maxConcurrent);
      
      console.log(`📦 Processing batch ${Math.floor(i / maxConcurrent) + 1}/${Math.ceil(pendingUploads.length / maxConcurrent)} (${batch.length} files)`);
      
      // Upload batch concurrently with improved callback handling
      const promises = batch.map(async (upload) => {
        try {
          // Notify start of upload
          opts.onFileStart(upload.id);
          console.log(`☁️ Starting S3 upload for ${upload.file.name} (${Math.round(upload.file.size / 1024)}KB)`);
          
          const response = await this.uploadFile(upload.file, upload.id, sessionId, sessionKey, {
            ...opts,
            onProgress: (progress, details) => {
              // Call file-specific progress callback
              opts.onFileProgress(upload.id, progress, details);
              
              // Also call general progress callback for compatibility
              if (options.onProgress) {
                options.onProgress(progress, details);
              }
            },
          });
          
          console.log(`✅ Upload complete: ${upload.file.name}`);
          
          // Notify completion and save metadata immediately
          await opts.onFileComplete(upload.id, response, upload);
          
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          console.error(`❌ Upload failed: ${upload.file.name}`, error);
          
          // Notify error
          opts.onFileError(upload.id, errorMessage);
        }
      });
      
      await Promise.all(promises);
      
      // Minimal delay between batches for iOS Safari memory management
      if (i + maxConcurrent < pendingUploads.length) {
        await new Promise(resolve => setTimeout(resolve, 100)); // Reduced from 500ms
      }
    }
    
    console.log(`🏁 Batch upload complete: all batches processed`);
  }

  static generateThumbnailUrl(
    originalUrl: string,
    width: number = 300,
    height?: number,
    quality: string = 'auto'
  ): string {
    try {
      const cloudinaryBasePattern = /https:\/\/res\.cloudinary\.com\/([^/]+)\/(image|video)\/upload\//;
      const match = originalUrl.match(cloudinaryBasePattern);
      
      if (!match) {
        return originalUrl;
      }

      const [fullMatch, cloudName, resourceType] = match;
      const afterUpload = originalUrl.substring(fullMatch.length);
      
      let transformations: string[] = [
        `w_${width}`,
        `q_${quality}`,
        'f_auto'
      ];
      
      if (height) {
        transformations.push(`h_${height}`, 'c_fill');
      } else {
        transformations.push('c_scale');
      }
      
      const transformString = transformations.join(',');
      
      return `https://res.cloudinary.com/${cloudName}/${resourceType}/upload/${transformString}/${afterUpload}`;
    } catch (error) {
      console.warn('Failed to generate thumbnail URL:', error);
      return originalUrl;
    }
  }
}
